import { useCallback, useState } from "react";
import { AxiosError } from "axios";
import { ConfirmDeviceCode } from "../../Api/DeviceAuth";

type UseConfirmDeviceCodeArgs = {
    getValidAccessToken: () => Promise<string | null>;
    /**
     * Compatibility path for activation URLs used by older TV clients.
     * It is attempted only when the modern auth service says the code does not
     * exist (404). Other modern responses remain authoritative.
     */
    legacyNotFoundFallback?: (
        rawCode: string,
        abortController: AbortController
    ) => Promise<boolean>;
};

type ConfirmDeviceCodeResult =
    | { ok: true }
    | { ok: false; error?: AxiosError | Error; status?: number; code?: string };

function normalizeDeviceCode(raw: string) {
    return raw.replace(/[^a-z0-9]/gi, "").toUpperCase();
}

export function useConfirmDeviceCode({
    getValidAccessToken,
    legacyNotFoundFallback,
}: UseConfirmDeviceCodeArgs) {
    const [isConfirming, setIsConfirming] = useState(false);
    const [lastErrorMessage, setLastErrorMessage] = useState<string | null>(null);

    const confirmDeviceCode = useCallback(
        async (rawCode: string): Promise<ConfirmDeviceCodeResult> => {
            const code = normalizeDeviceCode(rawCode);
            if (!code) {
                const err = new Error("Please enter a code.");
                setLastErrorMessage(err.message);
                return { ok: false, error: err };
            }

            const controller = new AbortController();
            setIsConfirming(true);
            setLastErrorMessage(null);

            try {
                const token = await getValidAccessToken();
                if (!token) {
                    const err = new Error("Not authenticated");
                    setLastErrorMessage(err.message);
                    return { ok: false, error: err };
                }

                const res = await ConfirmDeviceCode(code, token, controller);

                if (res === true) return { ok: true };

                const axiosErr = res as AxiosError;
                const status = axiosErr.response?.status;

                // Long-lived branded activation URLs predate the v1 device
                // flow. A narrowly scoped fallback lets an old bookmark accept
                // a current code while retaining support for old TV clients.
                if (status === 404 && legacyNotFoundFallback) {
                    const legacyConfirmed = await legacyNotFoundFallback(
                        rawCode.trim(),
                        controller
                    );
                    if (legacyConfirmed) return { ok: true };
                }

                let msg = "That code couldn’t be confirmed. Please try again.";

                if (status === 404) msg = "That code wasn’t found. Double-check and try again.";
                else if (status === 410) msg = "That code expired. Generate a new code on your TV app.";
                else if (status === 409) msg = "That code was already used. Generate a new code on your TV app.";
                else if (status === 401) msg = "You need to be logged in to link a TV device.";

                setLastErrorMessage(msg);
                return { ok: false, error: axiosErr, status };
            } catch (err) {
                setLastErrorMessage("That code couldn’t be confirmed. Please try again.");
                return { ok: false, error: err as Error };
            } finally {
                controller.abort();
                setIsConfirming(false);
            }
        },
        [getValidAccessToken, legacyNotFoundFallback]
    );

    const clearConfirmError = useCallback(() => setLastErrorMessage(null), []);

    return { confirmDeviceCode, isConfirming, lastErrorMessage, clearConfirmError };
}
